/**
 * AuthProvider Context
 * Auto-generated - Auth Tokens Infrastructure Component
 *
 * Stores tokens in memory (NOT localStorage) via tokenStorage.
 * Registers token getter/refresh with shared api-client for automatic auth.
 * Attempts silent refresh on mount if refresh token exists.
 *
 * NOTE: All paths are relative (e.g. "/auth/login", not "/api/v1/auth/login")
 * because api-client.ts already uses API_BASE_URL which includes /api/v1.
 */
import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import { tokenStorage } from "../utils/tokenStorage";
import { api, setAuthTokenGetter, setAuthRefreshFn } from "../core/api-client";
import type { AuthContextValue } from "../hooks/useAuth";

/** Decode the payload section of a JWT (base64url → JSON) */
function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return {};
  }
}

/** True when a JWT is still valid with >=30s of life left. */
function isAccessTokenFresh(token: string | null): boolean {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  const exp = typeof payload.exp === "number" ? payload.exp : 0;
  const nowSec = Math.floor(Date.now() / 1000);
  return exp - nowSec > 30;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
  onSessionExpired?: () => void;
}

export function AuthProvider({ children, onSessionExpired }: AuthProviderProps) {
  const [user, setUser] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Dedupe concurrent refresh calls so rotated refresh tokens aren't
  // double-spent (e.g. mount-init racing with an api-client 401 retry).
  const refreshInFlight = useRef<Promise<boolean> | null>(null);

  // JWT only carries sub/role/exp — fetch /users/me to enrich state with
  // name/email/etc. so the AppShell can show the user's first name after
  // a hard reload (otherwise the label falls back to 'Account').
  const hydrateProfile = useCallback(async () => {
    try {
      const data = await api.get<{ items?: Array<Record<string, unknown>> }>("/users/me");
      const me = data?.items?.[0];
      if (me) setUser((prev) => ({ ...(prev || {}), ...me }));
    } catch {
      /* non-fatal — keep whatever the JWT gave us */
    }
  }, []);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (refreshInFlight.current) return refreshInFlight.current;

    const currentRefreshToken = tokenStorage.getRefreshToken();
    if (!currentRefreshToken) return false;

    const run = (async () => {
      try {
        const data = await api.post("/auth/refresh", {
          refreshToken: currentRefreshToken,
        });
        const { accessToken, refreshToken: newRefreshToken } = data as any;
        tokenStorage.setTokens(accessToken, newRefreshToken);
        return true;
      } catch {
        tokenStorage.clearTokens();
        setUser(null);
        onSessionExpired?.();
        return false;
      } finally {
        refreshInFlight.current = null;
      }
    })();

    refreshInFlight.current = run;
    return run;
  }, [onSessionExpired]);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    const data = await api.post("/auth/login", { email, password });
    const { accessToken, refreshToken: newRefreshToken } = data as any;
    tokenStorage.setTokens(accessToken, newRefreshToken);

    // Decode JWT to populate user with sub, role, and other claims
    setUser({ email, ...decodeJwtPayload(accessToken) });
    void hydrateProfile();
  }, [hydrateProfile]);

  const logout = useCallback(async (): Promise<void> => {
    const currentRefreshToken = tokenStorage.getRefreshToken();
    if (currentRefreshToken) {
      try {
        await api.post("/auth/logout", { refreshToken: currentRefreshToken });
      } catch {
        // Logout best-effort; clear local state regardless
      }
    }
    tokenStorage.clearTokens();
    // Nuke any service-worker caches so the next tab / next login doesn't
    // read a cached snapshot of the previous user's project list, credits,
    // etc. Best-effort — failures are non-fatal.
    try {
      if (typeof caches !== "undefined") {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      /* swallow */
    }
    setUser(null);
  }, []);

  // Register token access with shared api-client
  useEffect(() => {
    setAuthTokenGetter(() => tokenStorage.getAccessToken());
    setAuthRefreshFn(async () => {
      const success = await refreshToken();
      return success ? tokenStorage.getAccessToken() : null;
    });
  }, [refreshToken]);

  // Attempt silent refresh on mount. Only call /auth/refresh if the access
  // token is actually expired — gratuitous refreshes rotate the server-side
  // refresh token and can trip theft-detection if a stale tab sends the
  // pre-rotation token afterwards.
  useEffect(() => {
    const init = async () => {
      if (tokenStorage.hasTokens()) {
        const accessToken = tokenStorage.getAccessToken();
        if (isAccessTokenFresh(accessToken)) {
          setUser(accessToken ? decodeJwtPayload(accessToken) : { authenticated: true });
          void hydrateProfile();
        } else {
          const success = await refreshToken();
          if (success) {
            const token = tokenStorage.getAccessToken();
            setUser(token ? decodeJwtPayload(token) : { authenticated: true });
            void hydrateProfile();
          }
        }
      }
      setIsLoading(false);
    };
    init();
  }, [refreshToken, hydrateProfile]);

  const value: AuthContextValue = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    login,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
